import React, { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { all_routes } from '../router/all_routes';
import API_URLS from '../../core/common/apiUrls';

interface FareCalendarData {
    Response?: {
        Destination: string;
        Error: {
            ErrorCode: number;
            ErrorMessage: string;
        };
        Origin: string;
        ResponseStatus: number;
        SearchResults: FareResult[];
        TraceId: string;
    };
}

interface FareResult {
    AirlineCode: string;
    AirlineName: string;
    BaseFare: number;
    DepartureDate: string;
    Fare: number;
    FuelSurcharge: number;
    IsLowestFareOfMonth: boolean;
    OtherCharges: number;
    Tax: number;
}

interface FlightSearchDetails {
    from: {
        iataCode: string;
        name: string;
    };
    to: {
        iataCode: string;
        name: string;
    };
    departureDate: string | Date;
    returnDate?: string | Date;
    cabinClass: string;
    passengers: {
        adults: number;
        children: number;
        infants: number;
    };
}

const FareCalender: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [fareData, setFareData] = useState<FareCalendarData | null>(null);
    const [loading, setLoading] = useState(true);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    // Get flight search details from location state
    const flightSearchDetails = location.state?.flightSearchDetails as FlightSearchDetails;
    const flightType = location.state?.flightType || "oneway";

    useEffect(() => {
        if (!flightSearchDetails) {
            setLoading(false);
            return;
        }
        fetchFareCalendar();
    }, [flightSearchDetails]);

    useEffect(() => {
        updateScrollButtons();
        if (scrollContainerRef.current) {
            scrollContainerRef.current.addEventListener('scroll', updateScrollButtons);
        }
        return () => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.removeEventListener('scroll', updateScrollButtons);
            }
        };
    }, []);

    const updateScrollButtons = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
        }
    };

    const getDepartureTimeFormat = (date: string | Date) => {
        const hour = dayjs(date).hour();
        if (hour >= 5 && hour < 12) return '08:00:00';
        if (hour >= 12 && hour < 17) return '14:00:00';
        if (hour >= 17 && hour < 21) return '19:00:00';
        if (hour >= 21 || hour < 5) return '01:00:00';
        return '00:00:00';
    };

    const fetchFareCalendar = async () => {
        setLoading(true);
        try {
            const requestPayload = {
                EndUserIp: "106.219.87.88",
                JourneyType: 1,
                PreferredAirlines: null,
                Segments: [{
                    Origin: flightSearchDetails.from.iataCode,
                    Destination: flightSearchDetails.to.iataCode,
                    FlightCabinClass: (() => {
                            switch (flightSearchDetails.cabinClass) {
                                case 'Economy': return 2;
                                case 'Premium Economy': return 3;
                                case 'Business': return 4;
                                case 'Premium Business': return 5;
                                case 'First': return 6;
                                default: return 1;
                            }
                        })(),
                    PreferredDepartureTime: dayjs(flightSearchDetails.departureDate).format('YYYY-MM-DD') + 'T' + getDepartureTimeFormat(flightSearchDetails.departureDate)
                }]
            };

            const response = await fetch(API_URLS.FLIGHTS.CALENDAR_FARES, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestPayload),
            });

            if (response.ok) {
                const data: FareCalendarData = await response.json();
                if (data.Response?.ResponseStatus === 1) {
                    setFareData(data);
                }
            }
        } catch (err) {
            console.error('Error fetching fare calendar:', err);
        } finally {
            setLoading(false);
        }
    };

    const getFareForDate = (date: string) => {
        if (!fareData?.Response?.SearchResults) return null;
        return fareData.Response.SearchResults.find(result =>
            dayjs(result.DepartureDate).format('YYYY-MM-DD') === date
        );
    };

    const getLowestFare = () => {
        if (!fareData?.Response?.SearchResults) return 0;
        return Math.min(...fareData.Response.SearchResults.map(result => result.Fare));
    };

    const getHighestFare = () => {
        if (!fareData?.Response?.SearchResults) return 0;
        return Math.max(...fareData.Response.SearchResults.map(result => result.Fare));
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const getFarePercentage = (fare: number) => {
        const lowest = getLowestFare();
        const highest = getHighestFare();
        if (highest === lowest) return 50;
        return ((fare - lowest) / (highest - lowest)) * 100;
    };

    const getFareColor = (fare: number) => {
        const percentage = getFarePercentage(fare);
        if (percentage < 33) return 'bg-success';
        if (percentage < 66) return 'bg-warning';
        return 'bg-danger';
    };

    const handleDateClick = (date: string) => {
        if (!flightSearchDetails) return;
        const currentSelectedDate = dayjs(flightSearchDetails.departureDate).format('YYYY-MM-DD');
        if (date !== currentSelectedDate) {
            const enrichedDetails = {
                ...flightSearchDetails,
                departureDate: date
            };
            navigate(all_routes.flightList, {
                state: { flightSearchDetails: enrichedDetails, flightType }
            });
        }
    };

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 300;
            scrollContainerRef.current.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    // Generate 30 days starting from 4 days before departure date (but not before today)
    const departureDate = dayjs(flightSearchDetails?.departureDate || new Date()).startOf('day');
    const today = dayjs().startOf('day');
    const startDateCandidate = departureDate.subtract(4, 'day');
    const startDate = startDateCandidate.isBefore(today) ? today : startDateCandidate;

    const dates = Array.from({ length: 30 }, (_, i) => startDate.add(i, 'day'));

    if (!flightSearchDetails) return null;

    const currentSelectedDate = dayjs(flightSearchDetails.departureDate).format('YYYY-MM-DD');

    return (
        <div className="fare-calendar-component">
            <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center">
                            <i className="isax isax-calendar-1 text-primary fs-20 me-3"></i>
                            <div>
                                <h6 className="mb-1 text-dark fw-semibold">
                                    {flightSearchDetails.from.name} ({flightSearchDetails.from.iataCode}) → {flightSearchDetails.to.name} ({flightSearchDetails.to.iataCode})
                                </h6>
                                <p className="mb-0 text-muted fs-14">
                                    Selected: {dayjs(currentSelectedDate).format('ddd, MMM D, YYYY')}
                                </p>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <button
                                className={`btn btn-icon btn-sm ${canScrollLeft ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                                onClick={() => scroll('left')}
                                disabled={!canScrollLeft}
                                type="button"
                            >
                                <i className="fa-solid fa-chevron-left"></i>
                            </button>
                            <button
                                className={`btn btn-icon btn-sm ${canScrollRight ? 'btn-outline-primary' : 'btn-outline-secondary'}`}
                                onClick={() => scroll('right')}
                                disabled={!canScrollRight}
                                type="button"
                            >
                                <i className="fa-solid fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>

                    {/* Scrollable Dates Container */}
                    <div className="position-relative">
                        <div
                            ref={scrollContainerRef}
                            className="fare-dates-scrollable d-flex align-items-center gap-3 pb-2"
                            style={{
                                overflowX: 'auto',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                scrollBehavior: 'smooth'
                            }}
                        >
                            <style>{`.fare-dates-scrollable::-webkit-scrollbar { display: none; }`}</style>

                            {dates.map((date) => {
                                const dateKey = date.format('YYYY-MM-DD');
                                const fareResult = getFareForDate(dateKey);
                                const isSelected = currentSelectedDate === dateKey;
                                const isToday = date.isSame(dayjs(), 'day');

                                return (
                                    <div
                                        key={dateKey}
                                        className="flex-shrink-0"
                                        style={{ width: '100px' }}
                                    >
                                        <div
                                            className={`card border-0 cursor-pointer transition-all ${isSelected ? 'bg-primary text-white' : 'bg-light'}`}
                                            style={{
                                                minHeight: '120px',
                                                height: '170px',
                                                width: '100px',
                                                cursor: 'pointer',
                                                boxShadow: isSelected ? '0 4px 12px rgba(13,110,253,0.35)' : undefined,
                                            }}
                                            onClick={() => handleDateClick(dateKey)}
                                        >
                                            <div className="card-body p-2 text-center d-flex flex-column justify-content-between">
                                                {/* Date Header */}
                                                <div>
                                                    <div className={`fw-bold fs-16 ${isSelected ? 'text-white' : 'text-dark'}`}>
                                                        {date.format('DD')}
                                                    </div>
                                                    <div className={`fs-12 ${isSelected ? 'text-white' : 'text-muted'}`} style={{ opacity: isSelected ? 0.8 : 1 }}>
                                                        {date.format('MMM')}
                                                    </div>
                                                    <div className={`fs-11 ${isSelected ? 'text-white' : 'text-muted'}`} style={{ opacity: isSelected ? 0.8 : 1 }}>
                                                        {date.format('ddd')}
                                                    </div>
                                                </div>

                                                {/* Fare Information */}
                                                <div className="mt-2">
                                                    {loading ? (
                                                        <div className="d-flex justify-content-center">
                                                            <div
                                                                className={`spinner-border spinner-border-sm ${isSelected ? 'text-white' : 'text-primary'}`}
                                                                style={{ width: '1rem', height: '1rem' }}
                                                            >
                                                                <span className="visually-hidden">Loading...</span>
                                                            </div>
                                                        </div>
                                                    ) : fareResult ? (
                                                        <>
                                                            {/* Fare Indicator Bar */}
                                                            <div className="mb-2">
                                                                <div
                                                                    className={`fare-indicator rounded-pill mx-auto ${isSelected ? 'bg-white' : getFareColor(fareResult.Fare)}`}
                                                                    style={{
                                                                        height: '4px',
                                                                        width: '100%',
                                                                        maxWidth: '40px',
                                                                        opacity: isSelected ? 0.7 : 1,
                                                                    }}
                                                                />
                                                            </div>

                                                            {/* Fare Price */}
                                                            <div className={`fw-semibold fs-14 ${isSelected ? 'text-white' : 'text-dark'}`}>
                                                                {formatCurrency(fareResult.Fare)}
                                                            </div>

                                                            {/* Lowest Fare Badge */}
                                                            {fareResult.IsLowestFareOfMonth && (
                                                                <span
                                                                    className="badge fs-10 mt-1"
                                                                    style={{
                                                                        background: isSelected ? 'rgba(255,255,255,0.25)' : '#d1fae5',
                                                                        color: isSelected ? '#fff' : '#065f46',
                                                                    }}
                                                                >
                                                                    Lowest
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <div className="mb-2">
                                                                <div
                                                                    className="rounded-pill mx-auto"
                                                                    style={{ height: '4px', width: '40px', background: isSelected ? 'rgba(255,255,255,0.3)' : '#dee2e6' }}
                                                                />
                                                            </div>
                                                            <div className={`fs-12 ${isSelected ? 'text-white' : 'text-muted'}`} style={{ opacity: isSelected ? 0.6 : 1 }}>
                                                                —
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Fare Legend */}
                    {fareData?.Response?.SearchResults && (
                        <div className="d-flex justify-content-center gap-4 mt-3 pt-2 border-top">
                            <div className="d-flex align-items-center gap-2 fs-12 text-muted">
                                <span className="bg-success rounded-pill d-inline-block" style={{ width: '16px', height: '4px' }}></span>
                                Low Fare
                            </div>
                            <div className="d-flex align-items-center gap-2 fs-12 text-muted">
                                <span className="bg-warning rounded-pill d-inline-block" style={{ width: '16px', height: '4px' }}></span>
                                Medium Fare
                            </div>
                            <div className="d-flex align-items-center gap-2 fs-12 text-muted">
                                <span className="bg-danger rounded-pill d-inline-block" style={{ width: '16px', height: '4px' }}></span>
                                High Fare
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FareCalender;